import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';

export default function About() {
  return (
    <div className="min-h-screen bg-[#f5f2f0]">
      {/* Hero Section - Same as Homepage */}
      <section className="relative min-h-[720px] flex items-center">
        {/* Background Image/Video */}
        <div className="absolute inset-0">
          <Image
            src="/hero-banner.jpg"
            alt="Women working out in gym"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#45394B]/60" />
        </div>

        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-50">
          <Header />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              About Female Fitness Finder
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-xl">
              Female Fitness Finder was born from a simple observation: women deserve fitness solutions that truly understand their unique needs, goals, and challenges.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {/* Story Section */}
        <div className="bg-white rounded-[24px] p-8 md:p-12 mb-8 shadow-sm">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px] rounded-[20px] overflow-hidden">
              <Image
                src="/about-image.jpg"
                alt="Women working out together"
                fill
                className="object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-[#8a7490] tracking-wide uppercase mb-4">Our Story</p>
              <h2 className="text-3xl font-bold text-[#2b262e] mb-4">Empowering Women Through Fitness</h2>
              <div className="space-y-4 text-[#6e6672] leading-relaxed">
                <p>
                  We're more than just a platform - we're a movement dedicated to empowering women through fitness. Our mission is to create a supportive, inclusive environment where women can discover the perfect fitness programs, connect with expert trainers, and join communities that celebrate their journey toward health and wellness.
                </p>
                <p>
                  Every gym, studio, and trainer on our platform is verified to ensure they provide a genuine women-only or women-focused environment, prioritizing spaces where women feel physically and emotionally safe.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#8a7490] rounded-[20px] p-8 text-white">
            <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
            <p className="text-white/90 leading-relaxed">
              To empower women to achieve their fitness goals by providing personalized, accessible, and supportive fitness solutions that adapt to their unique lifestyles and needs. We believe every woman deserves access to fitness spaces where she feels safe, supported, and empowered.
            </p>
          </div>

          <div className="bg-white rounded-[20px] p-8">
            <h2 className="text-2xl font-semibold text-[#2b262e] mb-4">Our Vision</h2>
            <p className="text-[#6e6672] leading-relaxed">
              To become the leading platform connecting women with fitness resources, creating a global community where every woman feels supported in her health and wellness journey. From yoga to strength training, we envision a world where every woman can find her perfect fitness space.
            </p>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-white rounded-[24px] p-8 md:p-12 mb-8">
          <h2 className="text-3xl font-bold text-[#2b262e] mb-10 text-center">Why Choose Us?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b262e] mb-2">Verified Experts</h3>
              <p className="text-[#6e6672]">All trainers are certified and experienced in women's fitness</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b262e] mb-2">Personalized Approach</h3>
              <p className="text-[#6e6672]">Programs tailored to your specific goals and body type</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b262e] mb-2">Supportive Community</h3>
              <p className="text-[#6e6672]">Connect with women on similar fitness journeys</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
